// テンプレート定義から自己完結HTMLを生成するディスパッチと、
// ロード済み iframe を「再ロードせず」更新するライブ DOM 操作。

import { FONT_HREF, FONT_STACKS, type DesignConfig, type FontKind } from "./design";
import { readableText } from "./color";
import {
  type BuildOpts,
  type TemplateDef,
  type Values,
  formatText,
  renderTemplate,
  resolveValues,
} from "./templates/shared";
import { TEMPLATES } from "./templates";

export { type BuildOpts } from "./templates/shared";

/**
 * config.template のテンプレート定義に、解決済みの値を流し込んで HTML を生成する。
 * 色・フォント・文言・画像はすべて config.values に入っており、fields がその意味づけを持つ。
 */
export function buildPreviewHtml(c: DesignConfig, opts: BuildOpts = {}): string {
  const def = TEMPLATES[c.template];
  return renderTemplate(def, resolveValues(def, c.values), opts);
}

/**
 * ロード済みプレビュー iframe を「再ロードせず」更新する。
 * テンプレートの fields を辿り、色は :root 変数、文言は data-bind、画像は data-img へ反映する。
 * これによりカラーピッカーのドラッグでも白フラッシュ・再パースが起きない。
 */
export function applyValues(doc: Document, def: TemplateDef, values: Values): void {
  const root = doc.documentElement;
  const v = resolveValues(def, values);
  for (const f of def.fields) {
    const val = v[f.key] ?? "";
    switch (f.kind) {
      case "color":
        root.style.setProperty(f.cssVar, val);
        if (f.onVar) root.style.setProperty(f.onVar, readableText(val));
        break;
      case "font":
        ensureFontLink(doc, val as FontKind);
        root.style.setProperty(f.cssVar, FONT_STACKS[val as FontKind] ?? val);
        break;
      case "text":
      case "textarea":
        setBound(doc, f.key, val, f.format);
        break;
      case "image":
        setImage(doc, f.key, val);
        break;
    }
  }
}

// data-bind 要素へ値を反映する。plain は textContent(矢印等の兄弟を保つ)、
// それ以外は budoux / 見出し強調入りの HTML を innerHTML で差し込む。
function setBound(
  doc: Document,
  key: string,
  value: string,
  format: "headline" | "inline" | "plain" | undefined,
): void {
  doc.querySelectorAll(`[data-bind="${key}"]`).forEach((el) => {
    if (format === "plain") {
      el.textContent = value; // textContent は自動エスケープされる
    } else {
      el.innerHTML = formatText(value, format);
    }
  });
}

// data-img スロットへ画像(data URL)を背景として反映する。
function setImage(doc: Document, key: string, url: string): void {
  doc.querySelectorAll<HTMLElement>(`[data-img="${key}"]`).forEach((el) => {
    if (url) {
      el.style.backgroundImage = `url("${url}")`;
      el.classList.add("has-img");
    } else {
      el.style.backgroundImage = "";
      el.classList.remove("has-img");
    }
  });
}

// 選択中の書体の Google Fonts <link> が無ければ追加する。iframe を再ロードせず
// フォントを切り替えるため、初期HTMLに無い書体は反映時に注入する。
function ensureFontLink(doc: Document, font: FontKind): void {
  if (doc.querySelector(`link[data-font="${font}"]`)) return;
  const link = doc.createElement("link");
  link.rel = "stylesheet";
  link.dataset.font = font;
  link.href = FONT_HREF[font];
  doc.head.appendChild(link);
}
